import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Persisted settings for the full-screen critical-alarm overlay (AlarmCenter).
 *
 * Kept separate from the voice (`useTtsStore`) settings: voice on/off is a
 * global control-room concern surfaced in the TopBar/NOC, whereas these govern
 * the PWA push-alarm UX (full-screen card, vibration, OS notification).
 */

export type AlarmThreshold = 2 | 3; // 2 = 경계+, 3 = 심각 only

interface AlarmSettings {
  /** Show the full-screen alarm card on a new qualifying alert. */
  overlayEnabled: boolean;
  setOverlayEnabled: (v: boolean) => void;
  /** Buzz the device (navigator.vibrate) when the card appears. */
  vibrate: boolean;
  setVibrate: (v: boolean) => void;
  /**
   * 경보음(`lib/alarmTone.ts`) — 카드가 뜰 때 음성 낭독 **앞에** 울리는 신호음.
   * 음성이 실패한 기기에서도 소리가 남게 하는 마지막 벨트라 기본 켬.
   */
  sound: boolean;
  setSound: (v: boolean) => void;
  /** Also raise an OS-level Notification when the tab is hidden. */
  notifyBrowser: boolean;
  setNotifyBrowser: (v: boolean) => void;
  /** Minimum level that triggers the full-screen card. */
  threshold: AlarmThreshold;
  setThreshold: (v: AlarmThreshold) => void;
  /**
   * 주의(1단계) 알림음(`lib/cautionChime.ts`) — 짧은 두 음, 음성 없음(2026-10-19, 10-12 검증 V-31).
   * **기본 끔**: 관제실 소음과 맞바꾸는 일이라 켤지는 사용자 결정이다. 경보음(`sound`)을 끄면 함께 울리지 않는다.
   */
  cautionChime: boolean;
  setCautionChime: (v: boolean) => void;
  /**
   * 화면·앱을 새로 열거나 새로고침했을 때, 최근 몇 분 안에 난 **미확인 경계·심각** 경보를 기준선에 묻지 않고
   * 경보창·음성을 다시 낸다(lib/alertBaseline.ts, 10-12 검증 V-22). **기본 끔** — 재표시 정책은 사용자 결정.
   * (푸시 알림을 눌러 들어온 그 경보는 이 설정과 상관없이 다시 띄운다.)
   */
  reshowRecent: boolean;
  setReshowRecent: (v: boolean) => void;
}

export const useAlarmSettings = create<AlarmSettings>()(
  persist(
    (set) => ({
      overlayEnabled: true,
      setOverlayEnabled: (overlayEnabled) => set({ overlayEnabled }),
      vibrate: true,
      setVibrate: (vibrate) => set({ vibrate }),
      sound: true,
      setSound: (sound) => set({ sound }),
      notifyBrowser: false,
      setNotifyBrowser: (notifyBrowser) => set({ notifyBrowser }),
      threshold: 3,
      setThreshold: (threshold) => set({ threshold }),
      // 새 키 두 개는 저장값에 없으면 아래 기본(끔)이 그대로 쓰인다 — persist 가 저장값을 기본 위에 얹는다.
      cautionChime: false,
      setCautionChime: (cautionChime) => set({ cautionChime }),
      reshowRecent: false,
      setReshowRecent: (reshowRecent) => set({ reshowRecent }),
    }),
    { name: "expertgas.alarm.v1" },
  ),
);
